import {
  IconBox,
  IconBuildingWarehouse,
  IconChartArrows,
  IconDatabase,
  IconForklift,
  IconLayoutDashboard,
  IconMapPin,
  IconPackageImport,
  IconTruckDelivery,
  IconUsers,
  IconBarcode,
  IconVaccineBottle,
  IconWaveSine,
  IconShieldLock,
  IconPackages,
  IconReceipt,
  IconCalendarDollar,
  IconTruck,
  IconSettings,
  IconStack2,
  IconAdjustments,
  IconArrowsExchange,
  IconClipboardList,
  IconTruckLoading,
  IconAddressBook,
  IconReceipt2,
  IconRoute,
  IconMapPins,
} from '@tabler/icons-react';

/** Any Tabler icon — they all share one component signature. */
type NavIcon = typeof IconBox;

interface NavRow {
  /**
   * Stable identity: the React key, and the handle a group's open state is kept under. A
   * destination can just use its own route; a group has no route, so it needs an id of its own.
   */
  id: string;
  labelKey: string;
  icon: NavIcon;
}

/** A row that goes somewhere. */
export interface NavDestination extends NavRow {
  to: string;
  /**
   * The permission that makes this row reachable; undefined means always shown. These are the READ
   * permissions each section's own endpoints require — a section whose data the user cannot load is
   * noise, not a locked door. Inventory Planning uses items:read because that is what its endpoints
   * check; they are item attributes, just warehouse-scoped ones.
   */
  permission?: string;
  children?: never;
}

/** A row that holds other rows. */
export interface NavGroup extends NavRow {
  children: NavNode[];
  to?: never;
  /**
   * Groups cannot carry one, deliberately. Visibility is derived from the children (see
   * `visibleNav`), which is the one thing the legacy design got right for free: a category existed
   * only because a row the user could see named it, so an empty group was impossible. A permission
   * declared here could only disagree with its own contents.
   */
  permission?: never;
}

/**
 * One row in the navigation drawer: either a destination or a group of them.
 *
 * The shape is recursive, so the depth is not capped. The legacy menu was: `tbl_WebMenu` carries a
 * `MenuCategory` and a `MenuSubCategory` as two plain string columns, so a third level would have
 * meant a third column, and its HTML was built by a loop that started a new group whenever the
 * category string changed — correct only for the exact `ORDER BY` it happened to be given.
 *
 * Two levels is still the convention here (see NAV_TREE); what changed is that a third costs
 * nothing but nesting one more array.
 */
export type NavNode = NavDestination | NavGroup;

/** The authenticated shell's own route, which every other route sits under. */
const HOME = '/app';

/**
 * The drawer, in the order it is read. Order is array order rather than a sort key on purpose:
 * legacy's `SortOrder` column sat third in `ORDER BY MenuCategory, MenuSubCategory, SortOrder`, so
 * it could only order the leaves inside one subcategory — the groups themselves came out
 * alphabetically no matter what anybody set. Here the order is the source, and a reviewer sees it
 * move in the diff.
 *
 * Groups are named for the job, and destinations keep the names their screens use, so no group
 * repeats its child's label. `nav.itemMaster` exists only for the menu: `nav.items` is also the
 * Items page heading, and the two want different words.
 */
export const NAV_TREE: NavNode[] = [
  { id: HOME, labelKey: 'nav.home', icon: IconLayoutDashboard, to: HOME },
  {
    id: 'group:operations',
    labelKey: 'nav.groups.operations',
    icon: IconForklift,
    children: [
      {
        id: '/app/inventory',
        labelKey: 'nav.inventory',
        icon: IconStack2,
        to: '/app/inventory',
        permission: 'inventory:read',
      },
      // Its own destination, not something reached through the inquiry screen. Adjusting stock is
      // the job, not a follow-up to looking at it, and the current product puts it on the menu for
      // that reason. It sits under inventory:adjust, so a read-only user does not see it at all.
      {
        id: '/app/inventory/adjust',
        labelKey: 'nav.adjustment',
        icon: IconAdjustments,
        to: '/app/inventory/adjust',
        permission: 'inventory:adjust',
      },
      // Beside Adjustment, not beneath the inquiry screen: moving stock is the job, not a
      // follow-up to looking at it. Same permission as adjusting — both write the ledger.
      {
        id: '/app/inventory/move',
        labelKey: 'nav.move',
        icon: IconArrowsExchange,
        to: '/app/inventory/move',
        permission: 'inventory:adjust',
      },
      // Beside Adjustment and Move. The current product gives production its own handheld action
      // rather than making somebody find the right transaction code, and the same reasoning puts it
      // on the menu here. Same permission as the other two — all three write the ledger.
      {
        id: '/app/inventory/production',
        labelKey: 'nav.productionReceipt',
        icon: IconTruckLoading,
        to: '/app/inventory/production',
        permission: 'inventory:adjust',
      },
      {
        // Under Operations rather than Sales: a pick is work on the floor. The person who opens
        // it is standing in the aisle, not looking at what a customer owes.
        id: '/app/picks',
        labelKey: 'nav.picks',
        icon: IconRoute,
        to: '/app/picks',
        permission: 'picks:read',
      },
      {
        // Beside Picks, not inside it: this is where somebody decides what the floor will be
        // asked to walk, which is a different job from walking it.
        id: '/app/pick-waves',
        labelKey: 'nav.pickWaves',
        icon: IconWaveSine,
        to: '/app/pick-waves',
        permission: 'picks:read',
      },
      {
        // Operations, not Setup: a lot is stock. The question this screen answers is what is
        // on the floor and what expires next.
        id: '/app/lots',
        labelKey: 'nav.lots',
        icon: IconVaccineBottle,
        to: '/app/lots',
        permission: 'lots:read',
      },
      {
        // Next to Lots, because they answer the same shape of question about two different
        // kinds of tracked stock, and somebody who knows where one lives will look for the
        // other beside it.
        id: '/app/serials',
        labelKey: 'nav.serials',
        icon: IconBarcode,
        to: '/app/serials',
        permission: 'serials:read',
      },
      {
        id: '/app/license-plates',
        labelKey: 'nav.licensePlates',
        icon: IconPackages,
        to: '/app/license-plates',
        permission: 'licenseplates:read',
      },
    ],
  },
  {
    id: 'group:items',
    labelKey: 'nav.groups.items',
    icon: IconDatabase,
    children: [
      {
        id: '/app/items',
        labelKey: 'nav.itemMaster',
        icon: IconBox,
        to: '/app/items',
        permission: 'items:read',
      },
      {
        id: '/app/planning',
        labelKey: 'nav.planning',
        icon: IconChartArrows,
        to: '/app/planning',
        permission: 'items:read',
      },
    ],
  },
  {
    // Suppliers sat at the top level while it was the only purchasing screen — a group of one gets
    // promoted away by visibleNav, so it could not be a group until it had company.
    id: 'group:purchasing',
    labelKey: 'nav.groups.purchasing',
    icon: IconTruck,
    children: [
      {
        id: '/app/purchase-orders',
        labelKey: 'nav.purchaseOrders',
        icon: IconClipboardList,
        to: '/app/purchase-orders',
        permission: 'po:read',
      },
      {
        // Under Purchasing rather than Inventory: a receipt exists against a purchase order, and
        // the person who raised the order is the one chasing what has turned up against it.
        id: '/app/receiving',
        labelKey: 'nav.receiving',
        icon: IconPackageImport,
        to: '/app/receiving',
        permission: 'receipts:read',
      },
      {
        id: '/app/suppliers',
        labelKey: 'nav.suppliers',
        icon: IconTruck,
        to: '/app/suppliers',
        permission: 'suppliers:read',
      },
    ],
  },
  {
    // The group Customers was waiting for. It was a top-level row while it was the only sales
    // screen, because the tree may not declare a group it cannot render as one -- the same path
    // Suppliers took into Purchasing.
    id: 'group:sales',
    labelKey: 'nav.groups.sales',
    icon: IconReceipt2,
    children: [
      {
        id: '/app/sales-orders',
        labelKey: 'nav.salesOrders',
        icon: IconReceipt2,
        to: '/app/sales-orders',
        permission: 'orders:read',
      },
      {
        id: '/app/customers',
        labelKey: 'nav.customers',
        icon: IconAddressBook,
        to: '/app/customers',
        permission: 'customers:read',
      },
    ],
  },
  {
    id: 'group:setup',
    labelKey: 'nav.groups.setup',
    icon: IconSettings,
    children: [
      {
        id: '/app/warehouses',
        labelKey: 'nav.warehouses',
        icon: IconBuildingWarehouse,
        to: '/app/warehouses',
        permission: 'warehouse:read',
      },
      {
        id: '/app/locations',
        labelKey: 'nav.locations',
        icon: IconMapPin,
        to: '/app/locations',
        permission: 'locations:read',
      },
      {
        // Beside Locations, because it is the same job seen from the item's side: the walk order
        // lives on the location and the face says which items are walked to there.
        id: '/app/pick-faces',
        labelKey: 'nav.pickFaces',
        icon: IconMapPins,
        to: '/app/pick-faces',
        permission: 'locations:read',
      },
      {
        // No permission: every signed-in user may read the codes, because every posting screen has
        // to offer them. Editing needs tenant:manage_settings, which the screen checks itself — the
        // list is worth seeing either way, and it is what a code means that people come here for.
        id: '/app/transaction-codes',
        labelKey: 'nav.transactionCodes',
        icon: IconReceipt,
        to: '/app/transaction-codes',
      },
      {
        // Who is in this tenant, what they may do, and which warehouses they may do it in.
        // Under Setup rather than its own group: it is configuration somebody visits when
        // a colleague joins, not a screen anybody works in.
        id: '/app/users',
        labelKey: 'nav.users',
        icon: IconUsers,
        to: '/app/users',
        permission: 'tenant:manage_users',
      },
      {
        // What permissions attach to. Beside Users because they are one job: who is here, and
        // what the groups they are in let them do.
        id: '/app/groups',
        labelKey: 'nav.accessGroups',
        icon: IconShieldLock,
        to: '/app/groups',
        permission: 'tenant:manage_users',
      },
      {
        // The delivery addresses a purchase order can be sent to. Under po:read rather than
        // tenant:manage_settings: the person raising the order is the one who knows a new dock
        // exists, which is the same reasoning as the endpoints.
        id: '/app/ship-tos',
        labelKey: 'nav.shipTos',
        icon: IconMapPin,
        to: '/app/ship-tos',
        permission: 'po:read',
      },
      {
        // Same reasoning: the supplier form has to offer the terms, so anybody may read them.
        id: '/app/payment-terms',
        labelKey: 'nav.paymentTerms',
        icon: IconCalendarDollar,
        to: '/app/payment-terms',
      },
      {
        // And the same again: ship confirm and the wave shipment both have to offer the carriers.
        id: '/app/ship-methods',
        labelKey: 'nav.shipMethods',
        icon: IconTruckDelivery,
        to: '/app/ship-methods',
      },
    ],
  },
];

/**
 * Does this destination cover the current screen — is it the route itself, or one below it?
 *
 * Matching on segment boundaries rather than a bare `startsWith`, which would light up Items for
 * `/app/items-archive` the day a route shares a prefix with another.
 */
export function isRouteActive(pathname: string, to: string): boolean {
  return pathname === to || pathname.startsWith(`${to}/`);
}

/** Every destination in the tree, in order, groups flattened away. */
function destinations(nodes: NavNode[]): NavDestination[] {
  return nodes.flatMap((node) => (node.children ? destinations(node.children) : [node]));
}

/**
 * The row the current screen belongs to: the most specific destination that covers it.
 *
 * Several can cover one screen at once, because routes nest — `/app` covers everything, and an
 * `/app/items` row would cover an `/app/items/import` row's screen too. Longest match wins, which
 * is what makes Home stop being a special case: it is simply always the shortest.
 */
export function activeDestinationId(nodes: NavNode[], pathname: string): string | undefined {
  let best: NavDestination | undefined;

  for (const node of destinations(nodes)) {
    if (isRouteActive(pathname, node.to) && (!best || node.to.length > best.to.length)) {
      best = node;
    }
  }

  return best?.id;
}

/**
 * The tree with everything the session cannot reach removed.
 *
 * Two rules beyond the obvious one:
 *
 * - a group with nothing visible under it disappears, rather than becoming an expander that opens
 *   onto nothing;
 * - a group left holding a single row is replaced by that row, so nobody has to expand a group to
 *   discover one destination. This is not hypothetical tidiness — permission filtering reaches it
 *   on its own. A user with `licenseplates:read` and no `inventory:read` would otherwise get
 *   "Operations" wrapping one link.
 */
export function visibleNav(
  nodes: NavNode[],
  isAllowed: (permission: string) => boolean,
): NavNode[] {
  const visible: NavNode[] = [];

  for (const node of nodes) {
    if (node.children) {
      const children = visibleNav(node.children, isAllowed);
      if (children.length === 0) {
        continue;
      }
      visible.push(children.length === 1 ? children[0] : { ...node, children });
      continue;
    }

    if (node.permission === undefined || isAllowed(node.permission)) {
      visible.push(node);
    }
  }

  return visible;
}

/** The groups above a given row, outermost first, or undefined if the row is not in this tree. */
function trailTo(nodes: NavNode[], id: string): string[] | undefined {
  for (const node of nodes) {
    if (node.id === id) {
      return [];
    }
    if (node.children) {
      const inner = trailTo(node.children, id);
      if (inner) {
        return [node.id, ...inner];
      }
    }
  }

  return undefined;
}

/**
 * The ids of the groups between the top of the tree and the current screen — what has to be open
 * for the current screen to be visible in the drawer, and what should read as active while it is
 * collapsed. The destination itself is not included; it carries its own active state.
 */
export function activeTrail(nodes: NavNode[], pathname: string): string[] {
  const active = activeDestinationId(nodes, pathname);

  return active === undefined ? [] : (trailTo(nodes, active) ?? []);
}
